export interface MarkdownHeader {
  level: number
  text: string
  id: string
}

/**
 * Extracts headers from markdown content and generates IDs for linking
 */
export function extractHeadersFromMarkdown(
  markdownContent: string,
): MarkdownHeader[] {
  const headers: MarkdownHeader[] = []
  const lines = markdownContent.split('\n')

  for (const line of lines) {
    // Match markdown headers (# Header, ## Header, etc.)
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/)
    if (headerMatch) {
      const level = headerMatch[1].length
      const text = headerMatch[2].trim()

      // Generate a URL-friendly ID from the header text
      const id = generateHeaderId(text)

      headers.push({
        level,
        text,
        id,
      })
      continue
    }

    // Match numbered list items with bold text (e.g., "1. **BOLD TEXT**")
    const numberedListMatch = line.match(/^(\d+)\.\s+\*\*(.+?)\*\*/)
    if (numberedListMatch) {
      const number = numberedListMatch[1]
      const text = numberedListMatch[2].trim()
      const id = generateHeaderId(text)

      // Treat numbered list items as level 2 headers in the TOC
      // Include the number in the display text
      headers.push({
        level: 2,
        text: `${number}. ${text}`,
        id,
      })
    }
  }

  return headers
}

/**
 * Generates a URL-friendly ID from header text
 */
export function generateHeaderId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}
